import { Container } from "reactstrap";
import { useMemo } from "react";
import cn from "classnames";
import styles from "./styles.module.scss";
import { jobs_fetcher } from "@/helpers/jobs_fetcher";
import { BsCashCoin } from "react-icons/bs";
import { IoLocationOutline } from "react-icons/io5";
import { GoTag } from "react-icons/go";
import Title from "@/ui/Title";
import { formatSalary } from "@/functions/formatSalary";

export const FeaturedJobCard = ({
  className,
  title = "",
  jobIds = [],
  buttonText = "View Job",
  buttonLinkBase = "/jobs",
  bgColor,
  titleColor,
  textColor,
}) => {
  const jobMetaMap = useMemo(() => {
    const map = {};
    jobIds.forEach(({ jobId, cardBGColor, cardTextColor, cardJobImage }) => {
      map[jobId] = { cardBGColor, cardTextColor, cardJobImage };
    });
    return map;
  }, [jobIds]);

  const formatUKDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();

    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return "th";
      switch (n % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    const dayWithSuffix = `${day}${getOrdinal(day)}`;

    const month = date.toLocaleString("en-GB", { month: "long" });
    const year = date.getFullYear();
    return `${dayWithSuffix} ${month} ${year}`;
  };

  const matchedJobs = useMemo(() => {
    try {
      const allJobs = jobs_fetcher.fetch({});
      const identifiers = jobIds.map((j) => j.jobId);
      if (!Array.isArray(allJobs)) return [];
      return allJobs.filter((job) => identifiers.includes(job.id) || identifiers.includes(job.external_reference));
    } catch (error) {
      console.error("Error fetching jobs:", error);
      return [];
    }
  }, [jobIds]);

  if (!matchedJobs.length) {
    return (
      null
    );
  }

  return (
    <section className={cn(styles.FeaturedJobCard, className, `bg-${bgColor}`)}>
      <Container>
        <Title title={title} className={cn(`text-${titleColor}`)} />
        <div className={cn(styles.wrapper, `text-${textColor}`)}>
          {matchedJobs.map((job) => {
            const {
              id,
              title,
              salary_package,
              location,
              published_at,
              url_slug,
              image,
              categories,
              external_reference,
              expires_at,
            } = job;
            const postedTime = formatUKDate(published_at);
            const categoryDetails = categories.map((cat) => ({
              name: cat.name,
              values: cat.values.map((val) => val.name).join(", "),
            }));
            const jobMeta = jobMetaMap[id] || jobMetaMap[external_reference] || {};
            // Trim whitespace from props to prevent invalid class names
            const cardBGColor = jobMeta.cardBGColor?.trim();
            const cardTextColor = jobMeta.cardTextColor?.trim();
            const cardJobImage = jobMeta.cardJobImage;
            const bgClass = cardBGColor ? `bg-${cardBGColor}` : "";
            const textClass = cardTextColor ? `text-${cardTextColor}` : "";

            return (
              <div key={id} className={cn(styles.card, bgClass, textClass)}>
                <div className={styles.info}>
                  <h3>{title}</h3>
                  <ul>
                    <li>
                      <BsCashCoin /> {formatSalary(salary_package)}
                    </li>
                    <li>
                      <IoLocationOutline /> {location}
                    </li>
                    {/* {categoryDetails.map((cat, index) => (
                      <li key={index}>
                        <GoTag />
                        <strong>{cat.name}:</strong> {cat.values}
                      </li>
                    ))} */}
                    {categoryDetails.map((cat, index) => (
                      <li key={index}>
                        <GoTag />
                        <strong>{cat.name === "Job Type" ? "Contract Type" : cat.name}:</strong> {cat.values}
                      </li>
                    ))}
                  </ul>
                  <p className="mb-0">{`Posted on: ${postedTime}`}</p>
                  <p>{`Application Deadline: ${formatUKDate(expires_at)}`}</p>
                  <a href={`${buttonLinkBase}/${url_slug}`} className={cn(styles.button, "btn", "btn-secondary")}>
                    {buttonText}
                  </a>
                </div>
                {(cardJobImage?.url || image?.url) && (
                  <div className={styles.image}>
                    <img src={cardJobImage?.url || image.url} alt={cardJobImage?.alt || image?.alt || "Job"} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default FeaturedJobCard;
